import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';

export type HistoryEventType = 'replaceState';
export interface HistoryEventData {
  as: 'hs';
  tp: 'history';
  ev: HistoryEventType;
  dt: unknown;
}
export type HistoryCallback = (data: unknown) => void;

const useWindowHistory = () => {
  const router = useRouter();
  const handleReplaceState = useCallback(
    (data: any, unused: string, url?: string | URL | null) => {
      if (typeof window !== 'undefined') {
        router.replace(`${url}`);
        if ('postMessage' in window) {
          window.postMessage(
            JSON.stringify({
              as: 'hs',
              tp: 'history',
              ev: 'replaceState',
              dt: { data, unused, url },
            } as HistoryEventData),
          );
        }
      }
    },
    [router],
  );

  const [stack, setStack] = useState<
    { id: string; eventType: HistoryEventType; callback: HistoryCallback }[]
  >([]);
  const handleAddListener = useCallback(
    (id: string, eventType: HistoryEventType, callback: HistoryCallback) => {
      setStack((values) => {
        const newValues = [...values];
        newValues.push({ id, eventType, callback });
        return newValues;
      });
    },
    [],
  );
  const handleRemoveListener = useCallback(
    (id: string, eventType: HistoryEventType) => {
      setStack((values) => {
        const newValues = [...values];
        let found = -1;
        let runTm = 10;
        do {
          found = _.findIndex(newValues, { id, eventType });
          newValues.splice(found, 1);
          runTm--;
        } while (found >= 0 && runTm >= 0);
        return newValues;
      });
    },
    [],
  );

  const handlePostMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as HistoryEventData;
        stack.forEach((item) => {
          if (
            payload.as === 'hs' &&
            payload.tp === 'history' &&
            payload.ev === item.eventType
          ) {
            item.callback(payload.dt as unknown);
          }
        });
      } catch (error) {}
    },
    [stack],
  );

  useEffect(() => {
    window.addEventListener('message', handlePostMessage);
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [handlePostMessage]);

  return {
    handleReplaceState,
    handleAddListener,
    handleRemoveListener,
  };
};

export default useWindowHistory;
